import { useCallback, useEffect, useState } from 'react';
import { Observable, Subject } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';

import { TimingConstants } from 'app/constants/timing-constants';

interface DebounceReturnType<I, R> {
    loading: boolean;
    result: R | undefined;
    setInput: (data: I) => void;
}

export const useDebounceObservable = <I, O, M = O>(
    handler: (data: I) => Observable<O>,
    mapper?: (result: O) => M,
): DebounceReturnType<I, M> => {
    const [delayedSubject] = useState(new Subject<I>());
    const [result, setResult] = useState<M | undefined>();
    const [loading, setLoading] = useState(false);

    const setInput = useCallback(
        (input: I) => {
            delayedSubject.next(input);
        },
        [delayedSubject],
    );

    useEffect(() => {
        const subscription = delayedSubject
            .pipe(debounceTime(TimingConstants.TYPING_DELAY))
            .subscribe((searchInput) => {
                setLoading(true);
                handler(searchInput)
                    .pipe(finalize(() => setLoading(false)))
                    .subscribe((data) => {
                        setResult(mapper?.(data) || (data as unknown as M));
                    });
            });

        return () => subscription.unsubscribe();
    }, [handler, delayedSubject, mapper]);

    return { loading, result, setInput };
};
